<template>
	<Pupup
		ref="popupRef"
		v-if="popup.show && pitchData"
		@close="handleClose"
		:showClose="true"
		:width="800"
		:class="{ 'pitch-to-collab-overlay': true, 'pitch-form-open': showPitchForm }"
		:shuffleUp="true"
		:noCloseFade="true"
		:transparent="true"
		:title="''">
		<template v-slot:content>
			<BrandPanel
				class="pitch-brand-popup-panel"
				:mini="false"
				:data="pitchData"
				:type="'pitch'"
				:auto-scroll-to-pitch-form-on-mount="!!popup.scrollToFormOnOpen"
				@scroll-to-pitch-form="scrollToPitchForm">
				<template v-slot:pitch-form>
					<div class="pitch-to-collab-contact pitch-to-collab-contact--floating" v-if="showPitchForm">
						<form class="pitch-to-collab-contact-inner" ref="pitchForm" @submit.prevent="">
							<h3 class="pitch-to-collab-contact-title">{{ pitchBrandTitle }}</h3>

							<div class="pitch-contact-summary" v-if="!showContactInputs">
								<div>
									<span class="pitch-contact-summary-label">Your brand &amp; contact details</span>
									<p v-for="line in contactSummaryLines" :key="line">{{ line }}</p>
								</div>
								<button
									type="button"
									class="button mini hollow clear no-margin"
									:disabled="isFormDisabled"
									@click="editContactDetails">
									Change
								</button>
							</div>

							<div class="pitch-contact-summary pitch-contact-summary--editing" v-if="showContactInputs">
								<span class="pitch-contact-summary-label">Your brand &amp; contact details</span>
								<FormInput
									type="text"
									v-model="sendPitch.userBrand"
									placeholder=""
									:label="labels.yourBrand"
									:required="true"
									:noMarginBottom="true"
									class="form-input"
									requiredText="*"
									:disabled="isFormDisabled" />

								<div class="form-input-wrapper">
									<FormInput
										type="text"
										v-model="sendPitch.userFirstName"
										placeholder=""
										:inline="true"
										:label="labels.yourFirstName"
										:required="true"
										:noMarginBottom="true"
										class="form-input"
										requiredText="*"
										:disabled="isFormDisabled" />

									<FormInput
										type="text"
										v-model="sendPitch.userSurname"
										placeholder=""
										:inline="true"
										:label="labels.yourSurname"
										:required="true"
										:noMarginBottom="true"
										class="form-input"
										requiredText="*"
										:disabled="isFormDisabled" />
								</div>

								<FormInput
									type="email"
									v-model="sendPitch.userEmail"
									placeholder=""
									:label="labels.yourEmail"
									:required="true"
									:noMarginBottom="true"
									class="form-input"
									requiredText="*"
									:disabled="isFormDisabled" />

								<button
									type="button"
									class="button mini hollow clear no-margin pitch-contact-summary-save"
									:disabled="isFormDisabled"
									@click="saveContactDetails">
									Save
								</button>
							</div>

							<FormInput
								type="textarea"
								v-model="userMessages[pitchData.id]"
								name="pitch-collab-message2"
								theRef="pitch-collab-message2"
								:label="labels.yourMessage"
								:autoResize="true"
								:autoResizeMaxHeight="240"
								:noMarginBottom="true"
								:required="true"
								:disabled="isFormDisabled"
								class="text-box"
								:key="messageKey"
								:placeholder="userMessagePlaceholder"
								requiredText="*" />

							<button
								type="button"
								:class="{ 'button flip-colours': true, disabled: loading }"
								:disabled="loading"
								@click="submitForm('pitch')"
								v-if="canPitch">
								{{ loading ? "Sending..." : labels.send }}
							</button>
							<p v-if="error" class="error-message" v-html="error" />

							<p class="info-statement" v-if="canPitch">
								All of the above information will be shared with {{ pitchBrand }}. If they accept your pitch, they will be in touch
								directly.
							</p>
							<p class="info-statement" v-if="!canPitch && !pitchData.featureOnly">
								<span class="note error" v-if="!hasSubscription"
									><strong>Note:</strong> You need a paid plan to send a Pitch to a brand. <br /><span
										class="button mini no-margin"
										style="margin-top: 12px"
										@click="subscribe()"
										>Start now</span
									><br /><br
								/></span>
							</p>
							<Loading position="inner" v-if="loading" />
						</form>
					</div>
				</template>
			</BrandPanel>
		</template>
	</Pupup>
</template>

<script>
import { ref, computed, watch, nextTick, onBeforeUnmount } from "vue";
import { useVueInstance } from "../../composables/useVueInstance";
import ApiService from "../../services/api.service";
import Pupup from "../Display/Pupup.vue";
import FormInput from "../Form/Input.vue";
import BrandPanel from "../Display/PitchBrandPanel.vue";
import LoadingSpinner from "../LoadingSpinner.vue";

export default {
	name: "PitchBrandPopup",
	components: {
		Pupup,
		FormInput,
		BrandPanel,
		Loading: LoadingSpinner,
	},
	setup() {
		const { store, route, router, $toast } = useVueInstance();
		const popupRef = ref(null);
		const pitchForm = ref(null);
		const pitches = ref([]);
		const loading = ref(false);
		const error = ref(null);
		const messageKey = ref(0);
		const userMessages = ref({});
		const contactDetailsEditable = ref(false);
		const sendPitch = ref({
			userFirstName: null,
			userSurname: null,
			userBrand: null,
			userEmail: null,
			message: null,
		});
		const userBrandName = ref("");

		const popup = computed(() => store.state.general.pitchBrandPopup || {});
		const brand = computed(() => store.state.general.brand);
		const subscription = computed(() => store.state.general.subscription);
		const featureOnly = computed(() => store.state.general.featureOnly);
		const hasSubscription = computed(() => subscription.value && subscription.value.active);

		const pitchData = computed(() => {
			if (popup.value.pitchData) return popup.value.pitchData;
			if (!popup.value.pitchId) return null;
			return pitches.value.find((pitch) => pitch.id === parseInt(popup.value.pitchId)) || null;
		});

		const labels = computed(() => {
			return {
				yourBrand: "Your brand",
				yourFirstName: "Your first name",
				yourSurname: "Your surname",
				yourEmail: "Your email",
				yourMessage: "Your message",
				send: "Send your pitch",
			};
		});

		const isAllowed = computed(() => {
			if (!hasSubscription.value) return false;
			return subscription.value.allowed ? subscription.value.allowed.pitch || false : false;
		});

		const canPitch = computed(() => {
			if (!pitchData.value) return false;
			return pitchData.value.featureOnly || isAllowed.value;
		});

		const isFormDisabled = computed(() => {
			if (!pitchData.value) return true;
			if (featureOnly.value) return true;
			if (pitchData.value.featureOnly) return false;
			return !isAllowed.value;
		});

		const showPitchForm = computed(() => {
			if (!pitchData.value) return false;
			if (pitchData.value.alreadyPitched) return false;
			if (featureOnly.value && pitchData.value.brand_id !== brand.value) return false;
			return true;
		});

		const pitchBrand = computed(() => (pitchData.value && pitchData.value.brand_name ? pitchData.value.brand_name : ""));
		const hasPitchContactDetails = computed(() => {
			return !!sendPitch.value.userBrand && !!sendPitch.value.userFirstName && !!sendPitch.value.userSurname && !!sendPitch.value.userEmail;
		});
		const showContactInputs = computed(() => contactDetailsEditable.value || !hasPitchContactDetails.value);
		const contactSummaryLines = computed(() => {
			const name = [sendPitch.value.userFirstName, sendPitch.value.userSurname].filter(Boolean).join(" ");
			return [sendPitch.value.userBrand, name, `<${sendPitch.value.userEmail || ""}>`].filter(Boolean);
		});
		const userMessagePlaceholder = computed(() => `Let ${pitchBrand.value} know why ${userBrandName.value} is a great fit for this collab...`);
		const pitchBrandTitle = computed(() => (pitchData.value ? `Pitch ${userBrandName.value} to ${pitchData.value.brand_name}` : ""));
		const popupTitle = computed(() => (pitchData.value ? `${userBrandName.value} Pitch to ${pitchData.value.brand_name}` : ""));

		const updateLoadingText = (payload) => store.commit("general/updateLoadingText", payload);
		const showSubscriptionPupup = (payload) => store.commit("general/showSubscriptionPupup", payload);

		let scrollElement = null;
		let resizeObserver = null;

		const updateScrollFade = () => {
			if (!scrollElement) return;

			const hasOverflow = scrollElement.scrollHeight - scrollElement.clientHeight > 4;
			const isAtBottom = scrollElement.scrollTop + scrollElement.clientHeight >= scrollElement.scrollHeight - 4;

			scrollElement.classList.toggle("scroll-fade-visible", hasOverflow && !isAtBottom);
		};

		const detachScrollTracking = () => {
			if (scrollElement) {
				scrollElement.removeEventListener("scroll", updateScrollFade);
				scrollElement.classList.remove("scroll-fade-visible");
			}

			if (resizeObserver) {
				resizeObserver.disconnect();
				resizeObserver = null;
			}

			scrollElement = null;
		};

		const attachScrollTracking = async () => {
			detachScrollTracking();
			await nextTick();

			const modalRoot = popupRef.value?.$el;
			scrollElement = modalRoot ? modalRoot.querySelector(".modal-inner-scroll") : null;

			if (!scrollElement) return;

			scrollElement.addEventListener("scroll", updateScrollFade, { passive: true });

			if (typeof ResizeObserver !== "undefined") {
				resizeObserver = new ResizeObserver(() => updateScrollFade());
				resizeObserver.observe(scrollElement);
				Array.from(scrollElement.children || []).forEach((child) => resizeObserver.observe(child));
			}

			updateScrollFade();
		};

		const getUserInfo = async () => {
			if (!brand.value) return;
			try {
				const response = await ApiService.get("pitch/user-info/" + brand.value);
				const userInfo = response.data;
				sendPitch.value.userBrand = userInfo.brand_name || "";
				sendPitch.value.userEmail = userInfo.email || "";
				sendPitch.value.userFirstName = userInfo.fname || "";
				sendPitch.value.userSurname = userInfo.sname || "";
				userBrandName.value = userInfo.brand_name || "";
			} catch (error) {
				console.error("Error fetching user info:", error);
			}
		};

		const getOpportunities = async () => {
			try {
				const response = await ApiService.get("pitch/opportunities");
				pitches.value = response.data || [];
			} catch (error) {
				console.error("Error fetching pitch opportunities:", error);
			}
		};

		const scrollToPitchForm = () => {
			if (pitchForm.value) {
				pitchForm.value.scrollIntoView({ behavior: "smooth" });
			}
		};

		const editContactDetails = () => {
			contactDetailsEditable.value = true;
		};

		const saveContactDetails = () => {
			if (!hasPitchContactDetails.value) {
				$toast.error(`Please fill in all required fields`);
				return;
			}

			contactDetailsEditable.value = false;
		};

		const subscribe = (isFeature = false) => {
			let ops = { paymentRedirect: popup.value?.paymentRedirect || "", title: "" };
			if (isFeature) {
				if (featureOnly.value) {
					ops.title = "";
					ops.pitchId = pitchData.value?.id || null;
					ops.featureOnly = true;
				}
			} else {
				ops.title = subscription.value.active ? "" : "You can't pitch your brand without a paid plan";
			}
			showSubscriptionPupup(ops);
		};

		const validateForm = (type) => {
			let requiredFields = [],
				err = false;
			if (pitchData.value && pitchData.value.brand_id === brand.value) {
				$toast.error(`You can't pitch to yourself`);
				return false;
			}
			if (type === "pitch") {
				requiredFields = ["userBrand", "userEmail", "userFirstName", "userSurname"];
			}
			const sourceObject = sendPitch.value;
			requiredFields.forEach((field) => {
				if (err) return;
				if (!sourceObject[field]) {
					err = true;
					$toast.error(`Please fill in all required fields`);
				}
			});
			if (!err) {
				if (pitchData.value && !userMessages.value[pitchData.value.id]) {
					$toast.error(`Please add your message`);
					err = true;
				}
			}
			return !err;
		};

		const submitForm = async (type) => {
			error.value = null;
			if (!loading.value && validateForm(type)) {
				try {
					loading.value = true;
					let submitData;
					let endpoint;
					if (type === "pitch") {
						submitData = {
							fname: sendPitch.value.userFirstName,
							sname: sendPitch.value.userSurname,
							email: sendPitch.value.userEmail,
							message: userMessages.value[pitchData.value.id],
							pitch_id: pitchData.value.id,
							brand_id: brand.value,
							brand_name: sendPitch.value.userBrand,
						};
						endpoint = "pitch/submit";
					}

					try {
						updateLoadingText(true);
						let response = await ApiService.post(endpoint, submitData);
						store.commit("general/updateVar", { varName: "subscription", info: response.data.subscription });
						updateLoadingText();
						store.commit("general/bumpPitchOpportunitiesRefresh");
						$toast.success(response.data.message);
						handleClose();
						getOpportunities();
					} catch (err) {
						updateLoadingText();
						console.error(`Error submitting ${type}:`, err);
						if (!!err.response && !!err.response.data && !!err.response.data.message) {
							error.value = err.response.data.message.message;
						} else {
							error.value =
								!!err.response && !!err.response.data && !!err.response.data.error
									? err.response.data.error
									: `Failed to submit ${type}. Please try again.`;
						}
					}
				} catch (err) {
					updateLoadingText();
					console.error(`Error submitting ${type}:`, err);
					error.value = `Failed to submit ${type}. Please try again.`;
				} finally {
					updateLoadingText();
					loading.value = false;
				}
			}
		};

		const handleClose = () => {
			const source = popup.value?.source || "";
			const returnTo = popup.value?.returnTo || "";
			store.commit("general/closePitchBrandPopup");
			if (source === "route" && route.path.startsWith("/opportunities/")) {
				if (returnTo) {
					router.replace(returnTo);
				} else {
					router.replace("/opportunities");
				}
			}
		};

		watch(
			() => popup.value.show,
			async (newVal) => {
				if (newVal) {
					getUserInfo();
					getOpportunities();
					messageKey.value += 1;
					await attachScrollTracking();
					// Track metadata for feedback widget
					if (popup.value.pitchId) {
						store.commit("feedback/setMetadata", {
							key: "creatingOpportunityId",
							value: popup.value.pitchId,
						});
						store.commit("feedback/setOpenWindow", "PitchBrandPopup");
					}
				} else {
					detachScrollTracking();
					// Clear metadata when closing
					store.commit("feedback/clearMetadata", "creatingOpportunityId");
					store.commit("feedback/removeOpenWindow", "PitchBrandPopup");
				}
			},
			{ immediate: true }
		);

		watch(
			() => popup.value.pitchId,
			async (newPitchId, oldPitchId) => {
				messageKey.value += 1;
				if (popup.value.show && newPitchId && newPitchId !== oldPitchId) {
					getOpportunities();
					await attachScrollTracking();
				}
			}
		);

		watch(
			() => pitchData.value,
			async (value) => {
				if (value && typeof userMessages.value[value.id] === "undefined") {
					userMessages.value[value.id] = "";
				}

				if (popup.value.show) {
					await attachScrollTracking();
				}
			}
		);

		watch(
			() => showPitchForm.value,
			async () => {
				if (popup.value.show) {
					await attachScrollTracking();
				}
			}
		);

		watch(
			() => popup.value.show,
			(newVal) => {
				if (!newVal) {
					error.value = null;
					loading.value = false;
					contactDetailsEditable.value = false;
				}
			}
		);

		onBeforeUnmount(() => {
			detachScrollTracking();
		});

		return {
			popupRef,
			popup,
			pitchData,
			pitchForm,
			labels,
			userMessages,
			sendPitch,
			messageKey,
			loading,
			error,
			hasSubscription,
			pitchBrand,
			contactSummaryLines,
			showContactInputs,
			userMessagePlaceholder,
			popupTitle,
			canPitch,
			isFormDisabled,
			showPitchForm,
			scrollToPitchForm,
			editContactDetails,
			saveContactDetails,
			submitForm,
			subscribe,
			handleClose,
			pitchBrandTitle,
		};
	},
};
</script>
<style lang="less" scoped>
@import "./../../assets/css/less/_settings.less";
@import "./../../assets/css/less/_helper.less";
.pitch-to-collab-contact {
	margin: 20px 0 0;
	.pitch-to-collab-contact-inner {
		position: relative;
		.pitch-to-collab-contact-title {
			margin: 0 0 10px;
			font-size: 1.2rem;
		}
	}
	.name-wrap {
		display: flex;
		flex-direction: row;
		flex-wrap: wrap;
		justify-content: space-between;

		.input-wrap {
			width: 48%;
		}
	}

	.form-input {
		margin-bottom: 20px;
	}

	.pitch-contact-summary {
		display: flex;
		align-items: center;
		justify-content: space-between;
		gap: 12px;
		margin: 0 0 16px;
		padding: 10px 12px;
		border: 1px solid @borderRose;
		border-radius: @radiusLg;
		background-color: @surfaceInputActive;

		p {
			margin: 2px 0 0;
			color: @textStrong;
			font-size: 0.9rem;
			line-height: 1.35;
			overflow-wrap: anywhere;
		}

		.button {
			flex-shrink: 0;
		}

		.form-input {
			margin-bottom: 12px;
		}

		.form-input-wrapper {
			width: 100%;
		}

		&.pitch-contact-summary--editing {
			display: block;

			.pitch-contact-summary-label {
				margin-bottom: 10px;
			}
		}
	}

	.pitch-contact-summary-label {
		display: block;
		color: @textSubtle;
		font-size: 0.72rem;
		font-weight: @bold;
		line-height: 1.2;
	}

	.pitch-contact-summary-save {
		display: block;
		margin-left: auto;
		width: fit-content;
	}

	.info-statement {
		font-size: 0.75rem;
		padding: 0;
		margin: 14px 0 0;
		.note {
			&.error {
				color: @red;
			}
		}
		span {
			&:not(.button) {
				color: inherit;
			}
		}
	}
	&.pitch-to-collab-contact--floating {
		width: auto;
		max-height: none;
		overflow: visible;
		margin: 40px 20px 0;
	}
}
@media screen and (min-width: 768px) {
	.pitch-to-collab-contact {
		margin: 0;
	}
	.form-input-wrapper {
		display: flex;
		flex-direction: row;
		justify-content: space-between;
		> * {
			width: 48%;
			margin-top: 0;
		}
	}
}
@media screen and (min-width: 1200px) {
	.pitch-to-collab-contact {
		min-width: 350px;

		&.pitch-to-collab-contact--floating {
			position: fixed;
			top: 120px;
			right: 24px;
			width: 360px;
			max-height: calc(100vh - 150px);
			overflow: auto;
			z-index: 2100;
			margin: 0;
			padding: 0;
		}

		&.pitch-to-collab-contact--floating .pitch-to-collab-contact-inner {
			border: 1px solid fade(@formDefaultColour, 18%);
			border-radius: @radiusLg;
			box-shadow: 0 4px 24px -2px rgba(103, 157, 162, 0.12);
			background-color: #fff;
			padding: 12px 20px 20px;
			margin: 0;
		}
	}
}

@media screen and (min-width: 1600px) {
	.pitch-to-collab-contact {
		&.pitch-to-collab-contact--floating {
			right: calc(50% - 775px);
		}
	}
}
</style>
<style lang="less">
.modal.pitch-to-collab-overlay .pitch-brand-popup-panel.brand-panel {
	box-shadow: none;
}

.modal.pitch-to-collab-overlay .modal-inner-scroll.scroll-fade-visible::after {
	content: "";
	position: sticky;
	display: block;
	left: 0;
	bottom: 0;
	width: 100%;
	height: 48px;
	margin-top: -48px;
	pointer-events: none;
	background: linear-gradient(180deg, rgba(255, 255, 255, 0), rgba(255, 255, 255, 0.88) 65%, #fff 100%);
	z-index: 2;
}

@media (min-width: 1200px) and (hover: hover) and (pointer: fine) {
	.modal.pitch-to-collab-overlay.pitch-form-open .modal-wrap {
		padding-right: 400px;

		.modal-inner {
			margin: 0 0 0 auto;
		}
	}
}
@media (min-width: 1600px) and (hover: hover) and (pointer: fine) {
	.modal.pitch-to-collab-overlay.pitch-form-open .modal-wrap {
		padding-right: 400px;

		.modal-inner {
			margin: 0 0 0 calc(50vw - 420px);
		}
	}
}
</style>
